import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { XMarkIcon, MagnifyingGlassIcon, SpeakerWaveIcon, PlayIcon, PauseIcon } from '@heroicons/react/24/outline';
import { IDialogueTone, PaginationQuery, DialogueToneQuery, Gender, AgeGroup, Language, GenderLabels, AgeGroupLabels, LanguageLabels, getLabelsByLocale, Locale } from '@packages/global';
import { dialogueToneApi } from '../../services/user/dialogueTone.api';
import { voiceApi } from '../../services/user/voice.api';
import Pagination from '../Pagination';

interface DialogueToneSelectorProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (tone: IDialogueTone) => void;
}

type TabType = 'system' | 'my';

export default function DialogueToneSelector({ isOpen, onClose, onSelect }: DialogueToneSelectorProps) {
  const { t, i18n } = useTranslation(['common_ui', 'common']);
  const [activeTab, setActiveTab] = useState<TabType>('system');
  const [tones, setTones] = useState<IDialogueTone[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedTone, setSelectedTone] = useState<IDialogueTone | null>(null);
  const [selectedGender, setSelectedGender] = useState<Gender | undefined>(undefined);
  const [selectedAgeGroup, setSelectedAgeGroup] = useState<AgeGroup | undefined>(undefined);
  const [selectedLanguage, setSelectedLanguage] = useState<Language | undefined>(undefined);
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({ total: 0, totalPages: 0, page: 1, limit: 15 });
  const [playingId, setPlayingId] = useState<string | null>(null);
  const audioRefs = useRef<{ [key: string]: HTMLAudioElement }>({});

  const loadSystemTones = async (page: number = 1) => {
    setLoading(true);
    try {
      const queryData: PaginationQuery<DialogueToneQuery> = {
        query: {
          search: search || undefined,
          gender: selectedGender,
          ageGroup: selectedAgeGroup,
          language: selectedLanguage
        },
        pagination: { page, limit: 15, sortBy: 'seq', sortOrder: 'asc' }
      };
      const response = await dialogueToneApi.dialogueTonePageList(queryData);
      setTones(response.items);
      setPagination(response.pagination);
      setCurrentPage(page);
    } catch (error) {
      console.error('加载音色失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadMyTones = async (page: number = 1) => {
    setLoading(true);
    try {
      const response = await voiceApi.getMyVoices({
        query: { search: search || undefined },
        pagination: { page, limit: 15 }
      });
      setTones(response.items);
      setPagination(response.pagination);
      setCurrentPage(page);
    } catch (error) {
      console.error('加载个人音色失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadTones = (page: number = 1) => {
    if (activeTab === 'system') loadSystemTones(page);
    else loadMyTones(page);
  };

  useEffect(() => {
    if (isOpen) {
      setSelectedTone(null);
      setCurrentPage(1);
      loadTones(1);
    } else {
      Object.values(audioRefs.current).forEach(audio => {
        audio.pause();
        audio.currentTime = 0;
      });
      setPlayingId(null);
    }
  }, [isOpen, activeTab, search, selectedGender, selectedAgeGroup, selectedLanguage]);

  const switchTab = (tab: TabType) => {
    setActiveTab(tab);
    setSearch('');
    setSelectedGender(undefined);
    setSelectedAgeGroup(undefined);
    setSelectedLanguage(undefined);
    setSelectedTone(null);
  };

  const handlePlayPause = (tone: IDialogueTone) => {
    const audio = audioRefs.current[tone.id];
    if (!audio) return;
    if (playingId === tone.id) {
      audio.pause();
      setPlayingId(null);
    } else {
      Object.entries(audioRefs.current).forEach(([id, audioEl]) => {
        if (id !== tone.id) { audioEl.pause(); audioEl.currentTime = 0; }
      });
      audio.play();
      setPlayingId(tone.id);
    }
  };

  const handleAudioEnded = (toneId: string) => {
    if (playingId === toneId) setPlayingId(null);
  };

  const handleSelect = () => {
    if (selectedTone) { onSelect(selectedTone); onClose(); }
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[90]">
      <div className="bg-bg-surface rounded-lg w-[900px] h-[610px] flex flex-col border border-border-subtle shadow-2xl">

        {/* Header */}
        <div className="flex items-center p-4 border-b border-border-subtle">
          {/* Tab 切换 */}
          <div className="flex items-center gap-1">
            <button
              onClick={() => switchTab('system')}
              className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${activeTab === 'system'
                ? 'text-blue-400 bg-blue-500/10'
                : 'text-gray-400 hover:text-gray-200 hover:bg-bg-elevated'}`}
            >
              {t('common_ui:dialogueToneSelector.tabSystem')}
            </button>
            <button
              onClick={() => switchTab('my')}
              className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${activeTab === 'my'
                ? 'text-blue-400 bg-blue-500/10'
                : 'text-gray-400 hover:text-gray-200 hover:bg-bg-elevated'}`}
            >
              {t('common_ui:dialogueToneSelector.tabMy')}
            </button>
          </div>

          {/* 右侧筛选 */}
          <div className="flex items-center gap-3 ml-auto">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder={t('common_ui:dialogueToneSelector.searchPlaceholder')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-52 pl-9 pr-4 py-1.5 border border-border-subtle rounded-lg bg-bg-form text-gray-50 placeholder-gray-500 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {activeTab === 'system' && (
              <>
                <select
                  value={selectedLanguage || ''}
                  onChange={(e) => setSelectedLanguage(e.target.value as Language || undefined)}
                  className="px-3 py-1.5 border border-border-subtle rounded-lg bg-bg-form text-gray-50 text-sm focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">{t('common_ui:dialogueToneSelector.allLanguages')}</option>
                  {Object.entries(getLabelsByLocale(LanguageLabels, i18n.language as Locale)).map(([key, label]) => (
                    <option key={key} value={key}>{label}</option>
                  ))}
                </select>
                <select
                  value={selectedGender || ''}
                  onChange={(e) => setSelectedGender(e.target.value as Gender || undefined)}
                  className="px-3 py-1.5 border border-border-subtle rounded-lg bg-bg-form text-gray-50 text-sm focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">{t('common_ui:dialogueToneSelector.allGenders')}</option>
                  {Object.entries(getLabelsByLocale(GenderLabels, i18n.language as Locale)).map(([key, label]) => (
                    <option key={key} value={key}>{label}</option>
                  ))}
                </select>
                <select
                  value={selectedAgeGroup || ''}
                  onChange={(e) => setSelectedAgeGroup(e.target.value as AgeGroup || undefined)}
                  className="px-3 py-1.5 border border-border-subtle rounded-lg bg-bg-form text-gray-50 text-sm focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">{t('common_ui:dialogueToneSelector.allAges')}</option>
                  {Object.entries(getLabelsByLocale(AgeGroupLabels, i18n.language as Locale)).map(([key, label]) => (
                    <option key={key} value={key}>{label}</option>
                  ))}
                </select>
                {(selectedLanguage || selectedGender || selectedAgeGroup) && (
                  <button
                    onClick={() => { setSelectedLanguage(undefined); setSelectedGender(undefined); setSelectedAgeGroup(undefined); }}
                    className="px-3 py-1.5 text-xs text-gray-400 hover:text-gray-200 border border-border-subtle rounded-lg hover:border-border-default cursor-pointer transition-colors"
                  >
                    {t('common_ui:dialogueToneSelector.clearFilter')}
                  </button>
                )}
              </>
            )}

            <button onClick={onClose} className="text-gray-400 hover:text-gray-200 cursor-pointer">
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
            </div>
          ) : tones.length === 0 ? (
            <div className="text-center text-gray-400 py-12">
              <SpeakerWaveIcon className="h-12 w-12 mx-auto mb-3 text-gray-600" />
              <p>{activeTab === 'my' ? t('common_ui:dialogueToneSelector.emptyMy') : t('common_ui:dialogueToneSelector.emptySystem')}</p>
            </div>
          ) : (
            <div className="grid grid-cols-3 gap-3">
              {tones.map((tone) => (
                <div
                  key={tone.id}
                  onClick={() => setSelectedTone(tone)}
                  className={`relative cursor-pointer rounded-lg p-3 border-2 transition-all ${selectedTone?.id === tone.id || playingId === tone.id
                    ? 'border-blue-500 ring-2 ring-blue-400 bg-blue-50/5'
                    : 'border-border-subtle hover:border-border-default bg-bg-elevated/50'}`}
                >
                  <div className="flex items-center gap-3">
                    <div className="relative flex-shrink-0">
                      {tone.coverImageUrl ? (
                        <img src={tone.coverImageUrl} alt={tone.desc} className="w-12 h-12 object-cover rounded-full" />
                      ) : (
                        <div className="w-12 h-12 bg-bg-elevated rounded-full flex items-center justify-center">
                          <SpeakerWaveIcon className="h-6 w-6 text-gray-400" />
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm font-medium text-gray-50 truncate" title={tone.desc || tone.code}>
                        {tone.desc || tone.code}
                      </h3>
                      <div className="flex items-center gap-1.5 text-xs text-gray-400 mt-1 flex-wrap">
                        {activeTab === 'system' ? (
                          <>
                            <span>{tone.languageDesc}</span>
                            <span>•</span>
                            <span>{tone.genderDesc}</span>
                            <span>•</span>
                            <span>{tone.ageGroupDesc}</span>
                          </>
                        ) : (
                          <>
                            {tone.originalFileName && <span className="truncate max-w-[100px]">{tone.originalFileName}</span>}
                            {tone.duration ? <span>{Math.floor(tone.duration / 60)}:{String(tone.duration % 60).padStart(2, '0')}</span> : null}
                          </>
                        )}
                      </div>
                    </div>
                    {tone.previewAudioUrl && (
                      <button
                        onClick={(e) => { e.stopPropagation(); handlePlayPause(tone); setSelectedTone(tone); }}
                        className={`flex-shrink-0 rounded-full w-8 h-8 flex items-center justify-center transition-colors cursor-pointer ${playingId === tone.id
                          ? 'bg-blue-500 hover:bg-blue-600 text-white'
                          : 'bg-bg-elevated hover:bg-border-default text-gray-300'}`}
                      >
                        {playingId === tone.id ? <PauseIcon className="h-4 w-4" /> : <PlayIcon className="h-4 w-4" />}
                      </button>
                    )}
                  </div>
                  {tone.previewAudioUrl && (
                    <audio
                      ref={(el) => { if (el) audioRefs.current[tone.id] = el; }}
                      preload="metadata"
                      onEnded={() => handleAudioEnded(tone.id)}
                    >
                      <source src={tone.previewAudioUrl} type="audio/mpeg" />
                      <source src={tone.previewAudioUrl} type="audio/wav" />
                    </audio>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-4 border-t border-border-subtle bg-bg-elevated">
          <div className="flex-1">
            <Pagination
              currentPage={currentPage}
              totalPages={pagination.totalPages}
              total={pagination.total}
              limit={pagination.limit}
              onPageChange={loadTones}
              showInfo={false}
            />
          </div>
          <div className="flex gap-3">
            <button onClick={onClose} className="px-4 py-2 text-gray-300 bg-bg-surface border border-border-subtle rounded-lg hover:bg-bg-elevated cursor-pointer transition-colors">
              {t('common:cancel')}
            </button>
            <button
              onClick={handleSelect}
              disabled={!selectedTone}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer transition-colors"
            >
              {t('common_ui:dialogueToneSelector.confirm')}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
